import clsx from 'clsx'
import React from 'react'

export interface ListItemChangeEventDetail {
  value?: any
  checked: boolean
  label: string
}

interface ListItemProps extends Omit<React.HTMLAttributes<HTMLLIElement>, 'onChange'> {
  value?: any
  children?: React.ReactNode
  disabled?: boolean
  checked?: boolean
  selectable?: boolean
  label?: string
  onChangeListItem?: (detail: ListItemChangeEventDetail) => void
}

const getSlotText = (node?: React.ReactElement): string => {
  if (!node) return ''
  const children = node.props?.children
  return typeof children === 'string' ? children : ''
}

export const ListItem = ({
  value,
  children,
  disabled = false,
  checked = false,
  selectable = false,
  label,
  className,
  style,
  onClick,
  onChangeListItem,
  ...rest
}: ListItemProps) => {
  const childrenArray = React.Children.toArray(children) as React.ReactElement[]

  const leftSlot = childrenArray.find(child => child?.props?.slot === 'left')
  const labelSlot = childrenArray.find(child => child?.props?.slot === 'label')
  const captionSlot = childrenArray.find(child => child?.props?.slot === 'caption')
  const rightSlot = childrenArray.find(child => child?.props?.slot === 'right')

  const hasSlots = Boolean(leftSlot || labelSlot || captionSlot || rightSlot)
  const fallbackLabel = !hasSlots ? children ?? <span>{label}</span> : null

  const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
    if (disabled) return

    if (selectable) {
      onChangeListItem?.({
        value,
        checked: !checked,
        label: label || getSlotText(labelSlot),
      })
    }

    onClick?.(event)
  }

  return (
    <li
      role="option"
      aria-selected={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      className={clsx(
        'flex items-center justify-between gap-2 select-none transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        !checked && !disabled && 'hover:bg-[#f5f5f5]',
        checked && 'bg-[#dfb26d]',
        className,
      )}
      style={{
        padding: '5px 8px',
        width: '240px',
        borderRadius: '6px',
        minHeight: '32px',
        color: checked ? '#191919' : '#4c4c4c',
        ...style,
      }}
      {...rest}
    >
      {leftSlot && <span className="flex items-center">{leftSlot}</span>}

      <span className="flex flex-col min-w-0 flex-1">
        {labelSlot ?? fallbackLabel}
        {captionSlot}
      </span>

      {rightSlot && <span className="flex items-center">{rightSlot}</span>}
    </li>
  )
}
